/**
 * KpiQuad — 4-up KPI tiles with trend glyphs and an accessible SVG title.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface KpiQuadProps {
  namespace?: string;
  className?: string;
}

const CELLS = ['kpi1', 'kpi2', 'kpi3', 'kpi4'] as const;

const TREND = {
  up: { icon: TrendingUp, color: 'text-emerald-300' },
  down: { icon: TrendingDown, color: 'text-emerald-300' },
  flat: { icon: Minus, color: 'text-cyan-300' },
} as const;

export function KpiQuad({ namespace = 'marketing.diagrams.kpiQuad', className }: KpiQuadProps) {
  const t = useTranslations(namespace);
  return (
    <div
      role="img"
      aria-label={t('title')}
      className={cn('grid min-w-0 gap-4 sm:grid-cols-2 lg:grid-cols-4', className)}
    >
      {CELLS.map((cell, i) => {
        const trendKey = (t.has(`${cell}.trend`) ? t(`${cell}.trend`) : 'flat') as
          | 'up'
          | 'down'
          | 'flat';
        const trend = TREND[trendKey] ?? TREND.flat;
        const TrendIcon = trend.icon;
        return (
          <MotionReveal key={cell} direction="up" delay={i * 0.06} className="min-w-0">
            <div className="glass-card group relative min-w-0 overflow-hidden border-l-2 border-cyan-500/40 px-5 py-6 transition-shadow duration-300 hover:shadow-glow-cyan-sm">
              <div className="flex items-center justify-between">
                <p className="text-display text-3xl font-bold text-cyan-300 lg:text-4xl">
                  {t(`${cell}.value`)}
                </p>
                <TrendIcon
                  size={18}
                  aria-hidden="true"
                  className={cn('shrink-0', trend.color)}
                />
              </div>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{t(`${cell}.label`)}</p>
              <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>
          </MotionReveal>
        );
      })}
    </div>
  );
}
